import { getAdapter, hasAdapter } from './registry.js';

// 统一错误
function rejectAdapter(method) {
  return Promise.reject(new Error('[JXBridge] adapter not ready, cannot call ' + method));
}

/**
 * 设置分享内容（注册语义）
 * 适用于微信/企微等容器自带分享入口的平台
 * APP/浏览器平台调用此方法为 no-op
 * @param {Object} opts 分享参数
 *   - title: 标题
 *   - desc: 描述
 *   - link: 跳转链接
 *   - imgUrl: 缩略图
 *   - weixin/wxwork/...: 平台差异化覆盖字段（可选）
 * @returns {Promise<{platform:string, action:string}>}
 */
export function setShare(opts) {
  if (!hasAdapter()) return rejectAdapter('setShare');
  return getAdapter().setShare(opts || {});
}

/**
 * 主动触发分享
 * 适用于 APP/浏览器等需要 H5 主动唤起分享面板的平台
 * 微信/企微平台不支持主动触发，会 reject
 * @param {Object} opts 分享参数（必传）
 * @returns {Promise<{platform:string, action:string}>}
 */
export function share(opts) {
  if (!hasAdapter()) return rejectAdapter('share');
  if (!opts || typeof opts !== 'object') {
    return Promise.reject(new Error('[JXBridge] share 必须传入分享参数'));
  }
  return getAdapter().share(opts);
}

/**
 * 拍照 / 选本地照片
 * @param {Object} opts
 *   - sourceType: 'camera' | 'album' | 'any'，默认 'any'
 *   - count: 最多选择数量，默认 1
 * @returns {Promise<{files: Array<{base64: string, type: string}>}>}
 *   base64 为完整 DataURL；用户取消时 reject(new Error('cancel'))
 */
export function chooseImage(opts) {
  if (!hasAdapter()) return rejectAdapter('chooseImage');
  const adapter = getAdapter();
  if (typeof adapter.chooseImage !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 chooseImage'));
  }
  return adapter.chooseImage(opts || {});
}

/**
 * 设置网页标题
 * @param {string} title 标题文本
 * @returns {Promise<{platform:string, action:string}>}
 */
export function setPageTitle(title) {
  if (!hasAdapter()) return rejectAdapter('setPageTitle');
  if (typeof title !== 'string') {
    return Promise.reject(new Error('[JXBridge] setPageTitle 参数必须为字符串'));
  }
  const adapter = getAdapter();
  if (typeof adapter.setPageTitle !== 'function') {
    return Promise.reject(new Error('[JXBridge] adapter does not support setPageTitle'));
  }
  return adapter.setPageTitle(title);
}

/**
 * 关闭当前页面窗口
 * 各平台按各自能力实现关闭逻辑
 *
 * @param {Object} [opts] 可选参数
 *   - goHome: 仅 jiyibao 有效，关闭后是否回到 APP 首页，默认 false
 * @returns {Promise<{platform:string, action:string}>}
 */
export function closeWindow(opts) {
  if (!hasAdapter()) return rejectAdapter('closeWindow');
  const adapter = getAdapter();
  if (typeof adapter.closeWindow !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 closeWindow'));
  }
  try {
    return Promise.resolve(adapter.closeWindow(opts || {}));
  } catch (err) {
    return Promise.reject(err);
  }
}

/**
 * 获取 wx.config 所需的配置信息
 * SDK 按平台探测自动路由到对应后端签名接口，业务方无需关心平台差异
 *
 * @param {string} sceneCode 业务场景码
 * @param {string} url 用于签名的页面 URL（调用方负责去除 #hash 部分）
 * @param {string} [env] 环境标识：'dev' | 'tst' | 'uat' | 'pre' | 'prod'，默认 'uat'
 * @returns {Promise<{appId:string, timestamp:string, nonceStr:string, signature:string}>}
 *   字段已归一化，业务方拿到直接喂给 wxConfig
 */
export function getWxConfig(sceneCode, url, env) {
  if (!hasAdapter()) return rejectAdapter('getWxConfig');
  const adapter = getAdapter();
  if (typeof adapter.getWxConfig !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 getWxConfig'));
  }
  return adapter.getWxConfig(sceneCode, url, env);
}

/**
 * 调用 wx.config 注入权限验证配置
 * @param {Object} configObj getWxConfig 返回的配置对象
 * @param {Object} opts
 *   - jsApiList: 需要使用的 JS 接口列表，默认注册库内已使用的全部接口
 * @returns {Promise<string>} 成功 resolve('成功')，失败 reject(Error)
 */
export function wxConfig(configObj, opts) {
  if (!hasAdapter()) return rejectAdapter('wxConfig');
  const adapter = getAdapter();
  if (typeof adapter.wxConfig !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 wxConfig'));
  }
  return adapter.wxConfig(configObj, opts || {});
}

/**
 * 获取代理人信息
 * 调用 token/verify 接口，token 优先用业务方传入的，未传从 cookie JX_http_usertoken 取
 * SDK 按平台探测自动路由到对应适配器，后续可能出现平台差异化
 *
 * @param {string} [token] 业务方传入的 token（可选，未传时从 cookie JX_http_usertoken 取）
 * @param {string} [env] 环境标识：'dev' | 'tst' | 'uat' | 'pre' | 'prod'，默认 'uat'
 * @returns {Promise<Object>} 直接返回后端 res.data
 */
export function getAgentUserInfo(token, env) {
  if (!hasAdapter()) return rejectAdapter('getAgentUserInfo');
  const adapter = getAdapter();
  if (typeof adapter.getAgentUserInfo !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 getAgentUserInfo'));
  }
  return adapter.getAgentUserInfo(token, env);
}

/**
 * 获取五行数据
 * 调用 customer/getFiveElements 接口，入参 sceneCode + ecifNo（url 不传）
 * SDK 按平台探测自动路由到对应适配器，所有平台均支持
 *
 * @param {string} sceneCode 业务场景码
 * @param {string} ecifNo 客户号（业务方传入）
 * @param {string} [env] 环境标识：'dev' | 'tst' | 'uat' | 'pre' | 'prod'，默认 'uat'
 * @returns {Promise<Object>} 直接返回后端 res.data
 */
export function getFiveElements(sceneCode, ecifNo, env) {
  if (!hasAdapter()) return rejectAdapter('getFiveElements');
  const adapter = getAdapter();
  if (typeof adapter.getFiveElements !== 'function') {
    return Promise.reject(new Error('[JXBridge] 当前平台适配器不支持 getFiveElements'));
  }
  return adapter.getFiveElements(sceneCode, ecifNo, env);
}
